import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, ArrowLeft, Menu, X, MapPin, Facebook, Instagram } from "lucide-react";

const leftLinks = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services" },
  { label: "Gallery", to: "/gallery" },
];

const rightLinks = [
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
  { label: "Quote", to: "/quote" },
];

const menuLinks = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services" },
  { label: "Gallery", to: "/gallery" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

const navCls = (isActive) =>
  `relative font-mono text-base font-bold uppercase tracking-[0.16em] transition-colors duration-200 py-2 after:absolute after:left-0 after:bottom-0 after:h-[2px] after:bg-[#007bff] after:transition-[width] after:duration-300 ${
    isActive ? "text-[#007bff] after:w-full" : "text-slate-500 hover:text-[#007bff] after:w-0 hover:after:w-full"
  }`;

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  const goBack = () => {
    if (window.history.state?.idx > 0) navigate(-1);
    else navigate("/");
  };

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
        data-testid="main-navbar"
        className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 ${
          scrolled
            ? "bg-white/90 backdrop-blur-xl border-b border-[#007bff]/15 shadow-[0_8px_30px_-12px_rgba(0,51,102,0.15)]"
            : "bg-white/80 backdrop-blur-md border-b border-[#007bff]/10"
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 lg:px-10 h-[76px] md:h-[92px] flex items-center justify-between relative">
          {/* Mobile back arrow on subpages */}
          {!isHome && (
            <button
              onClick={goBack}
              data-testid="mobile-back-button"
              aria-label="Go back"
              className="md:hidden flex items-center justify-center w-11 h-11 text-[#003366] hover:text-[#007bff] transition-colors duration-200 shrink-0"
            >
              <ArrowLeft size={26} strokeWidth={2.5} />
            </button>
          )}

          {/* Desktop left links */}
          <nav className="hidden md:flex items-center gap-10 flex-1" data-testid="nav-links-left">
            {leftLinks.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.to === "/"} data-testid={`nav-link-${l.label.toLowerCase()}`} className={({ isActive }) => navCls(isActive)}>
                {l.label}
              </NavLink>
            ))}
          </nav>

          {/* Logo — bigger, centered on desktop; centered on mobile subpages */}
          <Link
            to="/"
            data-testid="nav-logo-link"
            className={`flex items-center justify-center ${
              !isHome ? "absolute left-1/2 -translate-x-1/2" : ""
            } md:absolute md:left-1/2 md:-translate-x-1/2`}
          >
            <img
              src="/logo.png"
              alt="Sami Fab — Aluminium Welding and Fabrication, Albany WA"
              className={`w-auto transition-[height] duration-300 ${scrolled ? "h-11 md:h-14" : "h-12 md:h-16"}`}
            />
          </Link>

          {/* Desktop right links + actions */}
          <div className="hidden md:flex items-center gap-10 flex-1 justify-end" data-testid="nav-links-right">
            {rightLinks.map((l) => (
              <NavLink key={l.to} to={l.to} data-testid={`nav-link-${l.label.toLowerCase()}`} className={({ isActive }) => navCls(isActive)}>
                {l.label}
              </NavLink>
            ))}
          </div>

          {/* Mobile hamburger — always visible */}
          <button
            onClick={() => setMenuOpen(true)}
            data-testid="mobile-menu-button"
            aria-label="Open menu"
            className="md:hidden flex items-center justify-center w-11 h-11 bg-[#003366] text-white hover:bg-[#007bff] transition-colors duration-200 shrink-0"
          >
            <Menu size={22} strokeWidth={2.5} />
          </button>
        </div>
      </motion.header>

      {/* Full-screen mobile menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            data-testid="mobile-menu-overlay"
            className="fixed inset-0 z-[70] bg-[#003366] flex flex-col md:hidden"
          >
            <div
              aria-hidden
              className="absolute inset-0 opacity-[0.06]"
              style={{
                backgroundImage:
                  "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
                backgroundSize: "56px 56px",
              }}
            />
            <div className="relative flex items-center justify-between px-5 h-[76px]">
              <img src="/logo-white-bg.png" alt="SAMI FAB aluminium fabrication logo" className="h-10 w-auto" />
              <button
                onClick={() => setMenuOpen(false)}
                data-testid="mobile-menu-close-button"
                aria-label="Close menu"
                className="flex items-center justify-center w-11 h-11 border border-white/30 text-white hover:bg-white hover:text-[#003366] transition-colors duration-200"
              >
                <X size={22} strokeWidth={2.5} />
              </button>
            </div>

            <nav className="relative flex-1 flex flex-col justify-center px-8" data-testid="mobile-menu-links">
              {menuLinks.map((l, i) => (
                <div key={l.to} className="overflow-hidden">
                  <motion.div
                    initial={{ y: "110%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1], delay: 0.12 + i * 0.06 }}
                  >
                    <Link
                      to={l.to}
                      data-testid={`mobile-menu-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className={`font-display font-black uppercase tracking-tight leading-[1.25] block py-1 text-4xl ${
                        location.pathname === l.to ? "text-white/40" : "text-white"
                      }`}
                    >
                      {l.label}
                    </Link>
                  </motion.div>
                </div>
              ))}
              <div className="overflow-hidden mt-5 pt-5 border-t border-white/15">
                <motion.div
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1], delay: 0.45 }}
                >
                  <Link
                    to="/quote"
                    data-testid="mobile-menu-link-request-a-quote"
                    className="font-display font-black uppercase tracking-tight leading-[1.25] block py-1 text-3xl text-[#4da3ff]"
                  >
                    Request a Quote
                  </Link>
                </motion.div>
              </div>
            </nav>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.55 }}
              className="relative px-8 pb-10"
            >
              <div className="flex items-center justify-between gap-4">
                <a
                  href="tel:0415464900"
                  data-testid="mobile-menu-phone"
                  className="flex items-center gap-2.5 text-white font-display font-black text-xl"
                >
                  <Phone size={18} className="text-[#4da3ff]" /> 0415 464 900
                </a>
                <div className="flex gap-2.5">
                  <a
                    href="https://www.facebook.com/share/1FPbwHzncB/?mibextid=wwXIfr"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="mobile-menu-facebook"
                    aria-label="Sami Fab on Facebook"
                    className="w-10 h-10 border border-white/25 flex items-center justify-center text-white hover:bg-[#007bff] hover:border-[#007bff] transition-colors duration-200"
                  >
                    <Facebook size={16} />
                  </a>
                  <a
                    href="https://www.instagram.com/samiotis.fab"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="mobile-menu-instagram"
                    aria-label="Sami Fab on Instagram"
                    className="w-10 h-10 border border-white/25 flex items-center justify-center text-white hover:bg-[#007bff] hover:border-[#007bff] transition-colors duration-200"
                  >
                    <Instagram size={16} />
                  </a>
                </div>
              </div>
              <p className="mt-3 flex items-center gap-2.5 text-blue-200/70 font-mono text-[10px] uppercase tracking-[0.25em]">
                <MapPin size={13} /> Albany WA — Great Southern
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
